import React, { useState, useMemo, useEffect } from 'react';
import {
    Search,
    ArrowLeft,
    Users,
    Wallet,
    UserCheck,
    Download,
    Copy,
    CheckCircle,
    Building
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { bondsAPI, custodianAPI, walletAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createResponseSelector } from '../../utils/apiHelpers';
import { useAuth } from '../../contexts/AuthContext';

const CustodianBondHolders = () => {
    const navigate = useNavigate();
    const { bondId } = useParams();
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [holderTypeFilter, setHolderTypeFilter] = useState('all');
    const [copiedAddress, setCopiedAddress] = useState(null);

    // Fetch bond data
    const { data: bondData, isPending: bondLoading } = useQuery({
        queryKey: ['bond', bondId],
        queryFn: () => bondsAPI.getBond(bondId),
        select: createResponseSelector('bond', {})
    });

    const bond = bondData || {};

    // Fetch holdings for this custodian from the holdings table
    const { data: holdingsData = [], isPending: holdingsLoading } = useQuery({
        queryKey: ['custodian-bond-holdings', bondId, user?.id],
        enabled: !!bondId && !!user?.id,
        queryFn: async () => {
            const resp = await custodianAPI.getHoldingsAsCustodian({ status: 'active' });
            const root = resp.data || resp;
            let allHoldings = [];
            if (Array.isArray(root?.data)) allHoldings = root.data;
            else if (Array.isArray(root)) allHoldings = root;

            // API already filters by custodian, so we just need to filter by bond and exclude Primary Subscriber holders
            return allHoldings.filter(h => {
                const holdingBondId = h.bondId?._id || h.bondId;
                const holderSnapshot = h.holderSnapshot || {};
                
                // Must match the bond
                if (String(holdingBondId) !== String(bondId)) return false;
                
                // Exclude inactive holdings (zero remaining tokens or holdingsEndDate set)
                if ((h.remainingTokenAmount || 0) <= 0 || h.holdingsEndDate) return false;
                
                // Exclude holdings where the holder is a Primary Subscriber (PS direct holdings)
                // Note: primarySubscriberId field is just for tracking, not for filtering
                const isPrimarySubscriberHolder = holderSnapshot.isPrimarySubscriber === true;
                
                return !isPrimarySubscriberHolder;
            });
        }
    });

    // custodianHoldings is just the holdingsData since it's already filtered by custodian
    const custodianHoldings = holdingsData;

    // Build holders list from holdings data - deduplicate by holderId
    const holders = useMemo(() => {
        if (!custodianHoldings || custodianHoldings.length === 0) return [];

        // Filter out inactive holdings (should already be filtered but double-check)
        const activeHoldings = custodianHoldings.filter(h => 
            (h.remainingTokenAmount || 0) > 0 && !h.holdingsEndDate
        );

        // Calculate total supply as the sum of all active holdings for this bond (under this custodian)
        const totalSupply = activeHoldings.reduce((sum, h) => sum + (h.remainingTokenAmount || 0), 0);

        // Group holdings by holderId to deduplicate
        const holderMap = new Map();

        activeHoldings.forEach((holding, idx) => {
            const holderSnapshot = holding.holderSnapshot || {};
            const investorSnapshot = holding.investorSnapshot || {};
            const amount = holding.remainingTokenAmount || 0;
            const holderId = holding.holderId?._id || holding.holderId;
            const investorId = holding.investorId?._id || holding.investorId;
            const custodianId = holding.custodianId?._id || holding.custodianId;

            // Determine if this is a direct custodian holding or sub-investor holding
            const isCustodianDirectHolding = holderSnapshot.isCustodian === true;
            const holderName = holderSnapshot.name || holderSnapshot.email || 'Unknown Holder';
            const investorName = investorSnapshot.name || investorSnapshot.email || 'Unknown Investor';

            // Use holderId as the key for deduplication
            const holderKey = holderId ? String(holderId) : `unknown-${idx}`;

            if (holderMap.has(holderKey)) {
                // Aggregate holdings for the same holder
                const existing = holderMap.get(holderKey);
                existing.holdingAmount += amount;
                existing.walletBalance += amount;
                // Update lastActivity to the most recent
                if (new Date(holding.updatedAt) > new Date(existing.lastActivity)) {
                    existing.lastActivity = holding.updatedAt;
                }
            } else {
                // Create new holder entry
                holderMap.set(holderKey, {
                    id: holderKey,
                    holderId: holderId,
                    investorId: investorId,
                    custodianId: custodianId,
                    // Use holder name as the primary display name
                    investorName: holderName,
                    holderName: holderName,
                    actualInvestorName: investorName,
                    walletAddress: holderSnapshot.walletAddress || 'N/A',
                    holderType: isCustodianDirectHolding ? 'Custodian' : 'Sub-Investor',
                    holdingAmount: amount,
                    holdingPercentage: 0, // Will be calculated after totalSupply is known
                    registrationDate: holding.holdingsStartDate || holding.createdAt,
                    lastActivity: holding.updatedAt,
                    status: holding.holdingsEndDate ? 'Inactive' : 'Active',
                    kycStatus: 'Verified', // Assume verified if they have holdings
                    walletBalance: amount,
                    isWhitelisted: true,
                    email: holderSnapshot.email || investorSnapshot.email || null,
                    accountType: null, // Can be added to snapshot if needed
                    approvalStatus: 'approved',
                    companyRegistrationNumber: null,
                    allocationRequestId: holding.allocationRequestId,
                    // Store both holder and investor info for display
                    showInvestorInfo: !isCustodianDirectHolding && holderId !== investorId
                });
            }
        });

        // Convert map to array, filter out holders with zero holdings, calculate percentages, and sort
        return Array.from(holderMap.values())
            .filter(holder => holder.holdingAmount > 0) // Only include holders with active holdings
            .map(holder => {
                holder.holdingPercentage = totalSupply > 0 ? ((holder.holdingAmount / totalSupply) * 100).toFixed(2) : '0.00';
                return holder;
            })
            .sort((a, b) => b.holdingAmount - a.holdingAmount);
    }, [custodianHoldings]);

    // Filter holders
    const filteredHolders = holders.filter(holder => {
        const matchesSearch = holder.investorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            holder.investorId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            holder.walletAddress.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType = holderTypeFilter === 'all' ||
            (holderTypeFilter === 'verified' && holder.kycStatus === 'Verified') ||
            (holderTypeFilter === 'pending' && holder.kycStatus === 'Pending') ||
            (holderTypeFilter === 'active' && holder.status === 'Active');

        return matchesSearch && matchesType;
    });

    // Calculate totals
    const totalHolding = filteredHolders.reduce((sum, holder) => sum + holder.holdingAmount, 0);
    const totalSupplyFromHoldings = custodianHoldings.reduce((sum, h) => sum + (h.remainingTokenAmount || 0), 0);
    const totalHoldingPercentage = totalSupplyFromHoldings > 0 ? ((totalHolding / totalSupplyFromHoldings) * 100).toFixed(2) : '0.00';

    const copyToClipboard = (text, type) => {
        navigator.clipboard.writeText(text);
        setCopiedAddress(type);
        setTimeout(() => setCopiedAddress(null), 2000);
    };

    if (bondLoading || holdingsLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holders..." />;
    }

    if (!bond._id) {
        return (
            <div className="text-center py-12">
                <p className="text-error-600">Bond not found</p>
                <button onClick={() => navigate(-1)} className="mt-4 btn-primary">
                    Go Back
                </button>
            </div>
        );
    }

    if (!custodianHoldings || custodianHoldings.length === 0) {
        return (
            <div className="text-center py-12">
                <Building className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No Holdings Found</p>
                <p className="text-secondary-600 mb-4">
                    You don't have any holdings for this bond.
                </p>
                <button onClick={() => navigate(-1)} className="btn-primary">
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bond Token Holders</h1>
                            <p className="text-sm text-gray-500">
                                {bond.bondName} • {bond.isin || 'Pending ISIN'}
                            </p>
                            {bond.bondNumber && (
                                <p className="text-sm text-gray-500 font-mono">
                                    Bond #: {bond.bondNumber}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => {/* TODO: Export functionality */ }}
                        className="btn-primary"
                    >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                    </button>
                </div>

                {/* Bond & Allocation Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                    <div className="card">
                        <div className="card-body">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 p-3 rounded-lg bg-primary-100">
                                    <Users className="w-6 h-6 text-primary-600" />
                                </div>
                                <div className="ml-4 min-w-0 flex-1">
                                    <p className="text-sm font-medium text-secondary-600 truncate">Token Holders</p>
                                    <p className="text-2xl font-bold">
                                        {filteredHolders.length}
                                    </p>
                                    <p className="text-xs text-primary-600 mt-1">
                                        Total number of holders
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                                    <Wallet className="w-6 h-6 text-success-600" />
                                </div>
                                <div className="ml-4 min-w-0 flex-1">
                                    <p className="text-sm font-medium text-secondary-600 truncate">Total Holdings</p>
                                    <p className="text-2xl font-bold">
                                        {formatCurrency(totalHolding)}
                                    </p>
                                    <p className="text-xs text-success-600 mt-1">
                                        Combined holdings value
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 p-3 rounded-lg bg-info-100">
                                    <Building className="w-6 h-6 text-info-600" />
                                </div>
                                <div className="ml-4 min-w-0 flex-1">
                                    <p className="text-sm font-medium text-secondary-600 truncate">Custodian Total Holdings</p>
                                    <p className="text-2xl font-bold">
                                        {formatCurrency(custodianHoldings.reduce((sum, h) => sum + (h.remainingTokenAmount || 0), 0))}
                                    </p>
                                    <p className="text-xs text-info-600 mt-1">
                                        Total holdings amount
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                                    <UserCheck className="w-6 h-6 text-success-600" />
                                </div>
                                <div className="ml-4 min-w-0 flex-1">
                                    <p className="text-sm font-medium text-secondary-600 truncate">Verified</p>
                                    <p className="text-2xl font-bold">
                                        {filteredHolders.filter(h => h.kycStatus === 'Verified').length}
                                    </p>
                                    <p className="text-xs text-success-600 mt-1">
                                        Verified status
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card">
                    <div className="card-body">
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                            <div className="lg:col-span-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search by holder name, ID, or wallet address..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-input pl-10 w-full"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-2">
                                <select
                                    value={holderTypeFilter}
                                    onChange={(e) => setHolderTypeFilter(e.target.value)}
                                    className="form-input w-full"
                                >
                                    <option value="all">All Holders</option>
                                    <option value="verified">Verified Only</option>
                                    <option value="pending">Pending Verification</option>
                                    <option value="active">Active Only</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Holders Table */}
                <div className="card">
                    <div className="card-header">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-gray-900">
                                Token Holders ({filteredHolders.length})
                            </h2>
                            <span className="text-sm text-secondary-600">
                                Total: {formatCurrency(totalHolding)} ({totalHoldingPercentage}% of custodian's allocation)
                            </span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead className="table-header">
                                <tr>
                                    <th className="table-header-cell">Holder</th>
                                    <th className="table-header-cell">Holdings</th>
                                    <th className="table-header-cell">Wallet</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {filteredHolders.length > 0 ? (
                                    filteredHolders.map((holder) => (
                                        <tr key={holder.id} className="table-row">
                                            <td className="table-cell align-top">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="font-medium text-gray-900">
                                                            {holder.investorName}
                                                        </div>
                                                        <span className={`text-[10px] tracking-wide uppercase px-2 py-0.5 rounded border ${holder.holderType === 'Custodian'
                                                            ? 'bg-blue-50 text-blue-700 border-blue-100'
                                                            : 'bg-green-50 text-green-700 border-green-100'
                                                            }`}>
                                                            {holder.holderType}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-dark-gray space-y-0.5">
                                                        {holder.holderId && (
                                                            <div><span className="text-dark-gray">Holder ID:</span> <code className="text-[10px] bg-secondary-50 px-1 rounded">{holder.holderId}</code></div>
                                                        )}
                                                        {holder.showInvestorInfo && holder.actualInvestorName && (
                                                            <div><span className="text-dark-gray">Investor:</span> {holder.actualInvestorName}</div>
                                                        )}
                                                        {holder.email && (
                                                            <div><span className="text-dark-gray">Email:</span> {holder.email}</div>
                                                        )}
                                                        {holder.accountType && (
                                                            <div><span className="text-dark-gray">Account:</span> {holder.accountType}</div>
                                                        )}
                                                        {holder.approvalStatus && (
                                                            <div><span className="text-dark-gray">Approval:</span> {holder.approvalStatus}</div>
                                                        )}
                                                        {holder.registrationDate && (
                                                            <div><span className="text-dark-gray">Created:</span> {formatDate(holder.registrationDate)}</div>
                                                        )}
                                                        {holder.companyRegistrationNumber && (
                                                            <div><span className="text-dark-gray">Reg #:</span> {holder.companyRegistrationNumber}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                <div>
                                                    <div className="font-medium text-gray-900">
                                                        {formatCurrency(holder.holdingAmount)}
                                                    </div>
                                                    <div className="text-sm text-secondary-500">
                                                        {holder.holdingPercentage}% of custodian's allocation
                                                    </div>
                                                    {/* Removed allocation percentage line */}
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                <div className="flex items-center space-x-2">
                                                    <code className="text-xs bg-secondary-100 px-2 py-1 rounded font-mono">
                                                        {holder.walletAddress.slice(0, 6)}...{holder.walletAddress.slice(-4)}
                                                    </code>
                                                    {holder.accountType && (
                                                        <span className="text-[10px] tracking-wide uppercase bg-primary-50 text-primary-700 px-2 py-0.5 rounded border border-primary-100">
                                                            {holder.accountType}
                                                        </span>
                                                    )}
                                                    <button
                                                        onClick={() => copyToClipboard(holder.walletAddress, holder.id)}
                                                        className="text-secondary-600 hover:text-secondary-700"
                                                    >
                                                        {copiedAddress === holder.id ? (
                                                            <CheckCircle className="w-4 h-4 text-success-600" />
                                                        ) : (
                                                            <Copy className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="text-xs text-dark-gray mt-1">
                                                    Balance: {formatCurrency(holder.walletBalance)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="table-cell text-center py-12">
                                            <div className="text-secondary-500">
                                                <Users className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                                                <p className="text-lg font-medium mb-2">No token holders found</p>
                                                <p className="text-sm">
                                                    {searchTerm
                                                        ? `No holders match your search "${searchTerm}"`
                                                        : 'No holders have been allocated tokens for this bond yet'
                                                    }
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustodianBondHolders;
